import React from 'react';
import { BaseModal } from './BaseModal';
import { ImageZoom } from './ImageZoom';

interface CommunicationInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CommunicationInfoModal({ isOpen, onClose }: CommunicationInfoModalProps) {
  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="What Is a Preference Center?"
      size="large"
    >
      <div className="space-y-6 text-gray-600 dark:text-gray-300">
        {/* Introduction */}
        <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
          <p className="mb-4">
            A preference center is a centralized space (outside of a cookie banner) where users can manage:
          </p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>How often they hear from you</li>
            <li>What topics they care about</li>
            <li>Which channels you use to reach them (email, SMS, push)</li>
            <li>Personal info like name, email, and account settings</li>
          </ul>
          <p className="mt-4">
            It's not about just checking a few cookie boxes—it's about building a long-term relationship with your audience.
          </p>
        </div>

        {/* Business Value */}
        <div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Why It Matters for Business</h3>
          <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
            <p className="mb-4">
              Too often, users hit "unsubscribe" and are gone forever. But what if instead of unsubscribing completely, they could simply:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Reduce email frequency</li>
              <li>Pause marketing temporarily</li>
              <li>Switch communication channels</li>
              <li>Update contact info without frustration</li>
            </ul>
            <p className="mt-4">
              This keeps them engaged on their terms—and keeps you compliant with GDPR, CCPA, and other privacy laws.
            </p>
          </div>
        </div>

        {/* Success Stories */}
        <div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Real-World Success Stories</h3>
          <div className="space-y-4">
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <p className="font-semibold mb-2">Walmart</p>
              <p>Walmart's preference center allows customers to manage their communication preferences across different departments like Grocery, Electronics, and Home & Garden. Users can choose email frequency, opt in to SMS alerts, and select their preferred store location for personalized offers.</p>
              <ImageZoom 
                src="https://kebgtyzuvrqfytplwpwa.supabase.co/storage/v1/object/sign/website-images/PREFERENCE%20CENTER/WALMART%20Preference%20center.png?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJ3ZWJzaXRlLWltYWdlcy9QUkVGRVJFTkNFIENFTlRFUi9XQUxNQVJUIFByZWZlcmVuY2UgY2VudGVyLnBuZyIsImlhdCI6MTc0NTQyODA3OCwiZXhwIjoyMDI5MjUyMDc4fQ.kO3wyHe3q__z3WG5pxLMbV115cHjBJljNu-M9vs2BRk"
                alt="Walmart Preference Center Dashboard"
                className="mt-4 w-full h-auto object-contain max-h-[300px]"
              />
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <p className="font-semibold mb-2">MLB</p>
              <p>MLB's preference center enables fans to customize their experience by selecting favorite teams, choosing content types (news, scores, highlights), and setting notification preferences for game updates and special offers.</p>
              <ImageZoom 
                src="https://kebgtyzuvrqfytplwpwa.supabase.co/storage/v1/object/sign/website-images/PREFERENCE%20CENTER/MLB%20preference%20center.png?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJ3ZWJzaXRlLWltYWdlcy9QUkVGRVJFTkNFIENFTlRFUi9NTEIgcHJlZmVyZW5jZSBjZW50ZXIucG5nIiwiaWF0IjoxNzQ1NDMyODkwLCJleHAiOjIwMjkyNTY4OTB9.5UHRhI3WSyVtWoUlCmTZlZmqM07RC9zy1K22bB1figE"
                alt="MLB Communication Settings Page"
                className="mt-4 w-full h-auto object-contain max-h-[300px]"
              />
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <p className="font-semibold mb-2">NFL Multi-Level Preference Management</p>
              <p>The NFL demonstrates a sophisticated multi-level preference center approach:</p>
              
              <div className="mt-4 space-y-6">
                <div>
                  <p className="font-medium text-gray-700 dark:text-gray-300 mb-2">League-Wide Preferences</p>
                  <p className="mb-2">The NFL's league-wide preference center provides fans with comprehensive control over their communication preferences across the entire NFL ecosystem. Users can manage subscriptions for NFL RedZone, NFL Network, NFL Game Pass, Fantasy Football, and NFL Shop communications all in one place.</p>
                  <ImageZoom 
                    src="https://kebgtyzuvrqfytplwpwa.supabase.co/storage/v1/object/sign/website-images/PREFERENCE%20CENTER/NFL%20League%20Prefs.png?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJ3ZWJzaXRlLWltYWdlcy9QUkVGRVJFTkNFIENFTlRFUi9ORkwgTGVhZ3VlIFByZWZzLnBuZyIsImlhdCI6MTc0NTQzMzQwMywiZXhwIjoyMDI5MjU3NDAzfQ.p7aQ4PyifYoEylFtXTRJI_Xd1J07yMUzUSzvLIE9s-E"
                    alt="NFL League-Wide Preference Center"
                    className="mt-2 w-full h-auto object-contain max-h-[300px]"
                  />
                </div>

                <div>
                  <p className="font-medium text-gray-700 dark:text-gray-300 mb-2">Team-Specific Preferences - Atlanta Falcons Example</p>
                  <p className="mb-2">Similar to a multi-brand preference center, individual teams maintain their own preference centers. The Atlanta Falcons' preference center allows fans to manage team-specific communications including game day updates, ticket offers, merchandise deals, and local event notifications.</p>
                  <ImageZoom 
                    src="https://kebgtyzuvrqfytplwpwa.supabase.co/storage/v1/object/sign/website-images/PREFERENCE%20CENTER/Falcons%20Prefs.png?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJ3ZWJzaXRlLWltYWdlcy9QUkVGRVJFTkNFIENFTlRFUi9GYWxjb25zIFByZWZzLnBuZyIsImlhdCI6MTc0NTQzMzE0MCwiZXhwIjoyMDI5MjU3MTQwfQ._ZFq5yjRV50Iz0of3OXeBqbaH-iSwwRPRs1stYRZrvU"
                    alt="Atlanta Falcons Team Preference Center"
                    className="mt-2 w-full h-auto object-contain max-h-[300px]"
                  />
                </div>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <p className="font-semibold mb-2">Dairy Queen</p>
              <p>Dairy Queen's preference center lets customers manage their Blizzard Fan Club preferences, including email frequency, birthday rewards, and local store promotions. Users can customize their experience while staying connected to their favorite treats.</p>
              <ImageZoom 
                src="https://kebgtyzuvrqfytplwpwa.supabase.co/storage/v1/object/sign/website-images/PREFERENCE%20CENTER/Dairy%20queen%20preference%20center.png?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJ3ZWJzaXRlLWltYWdlcy9QUkVGRVJFTkNFIENFTlRFUi9EYWlyeSBxdWVlbiBwcmVmZXJlbmNlIGNlbnRlci5wbmciLCJpYXQiOjE3NDU0MjgxMTAsImV4cCI6MjAyOTI1MjExMH0.ycS1JpMpogBzSct9HfBY3C8jbEiYkifFil-Cl3wuh1w"
                alt="DQ Promo Preferences"
                className="mt-4 w-full h-auto object-contain max-h-[300px]"
              />
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <p className="font-semibold mb-2">Clorox</p>
              <p>Clorox's preference center offers a comprehensive solution for managing marketing communications, product updates, and promotional offers. The interface allows users to set preferences for different Clorox brands and product categories while maintaining privacy compliance.</p>
              <ImageZoom 
                src="https://kebgtyzuvrqfytplwpwa.supabase.co/storage/v1/object/sign/website-images/PREFERENCE%20CENTER/CLOROX%20preference%20center.png?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJ3ZWJzaXRlLWltYWdlcy9QUkVGRVJFTkNFIENFTlRFUi9DTE9ST1ggcHJlZmVyZW5jZSBjZW50ZXIucG5nIiwiaWF0IjoxNzQ1NDI4MTM0LCJleHAiOjIwMjkyNTIxMzR9.3_aAs4oS2lJasgNNrWygew6tOWn07F6_D4zhg9Uqg_Y"
                alt="Clorox Preference Center"
                className="mt-4 w-full h-auto object-contain max-h-[300px]"
              />
            </div>
          </div>
        </div>

        {/* Integration Section */}
        <div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Integration Made Simple</h3>
          <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg">
            <p className="mb-4">All of this runs cleanly through OneTrust, with:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Easy drag-and-drop components</li>
              <li>Hosted or embedded preference centers</li>
              <li>Support for external links and modal-style interfaces</li>
              <li>Authentication logic that syncs with your existing login system</li>
              <li>Ability to update name, email, preferences, notification types, and more</li>
            </ul>
            
            <div className="mt-8">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Integration Script and Implementation</h4>
              <ImageZoom 
                src="https://kebgtyzuvrqfytplwpwa.supabase.co/storage/v1/object/sign/website-images/PREFERENCE%20CENTER/Preference%20Center%20Integration.png?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJ3ZWJzaXRlLWltYWdlcy9QUkVGRVJFTkNFIENFTlRFUi9QcmVmZXJlbmNlIENlbnRlciBJbnRlZ3JhdGlvbi5wbmciLCJpYXQiOjE3NDU0MzE1MTUsImV4cCI6MjAyOTI1NTUxNX0.ACAsFxyHQYMO6MduwehzuDiemAHOcqdrjrXgjbT8T1s"
                alt="OneTrust Integration Dashboard"
                className="w-full h-auto object-contain max-h-[300px]"
              />
            </div>
          </div>
        </div>
      </div>
    </BaseModal>
  );
}